import type { Store } from 'vuex'
import type { Router } from 'vue-router'
import { useAdminDB } from '@/composables/useAdminDB'
import { armForceLogoutWarning, setSessionRevokeResolver } from '@/composables/useForceLogout'
import type { AuthUser } from '@/types/auth'

// ── Rastreio de sessões abertas (Admin → Sessões — migração v30) ──────────
// Uma "sessão" aqui é 1 aba/navegador: session_id gerado uma vez por aba
// (sessionStorage — sobrevive a F5, não sobrevive a fechar a aba, mesmo
// espírito do SESSION_ACTIVE_KEY já usado em main.ts::checkStaleSession) e
// registrado no banco com heartbeat periódico. Sem isso, esta dashboard não
// tinha NENHUMA visão de sessão — só um log de "último acesso" (migração v9)
// sem ideia de quantas abas/dispositivos um agente tem abertos nem se ainda
// estão de fato ativos.

const SESSION_ID_KEY = 'wootdash_session_id'
const HEARTBEAT_MS   = 30_000

let sessionId: string | null = null
let heartbeatTimer: ReturnType<typeof setInterval> | null = null

/** Session ID desta aba — gerado uma vez, persistido em sessionStorage. */
export function getSessionId(): string {
  if (sessionId) return sessionId
  try {
    let id = sessionStorage.getItem(SESSION_ID_KEY)
    if (!id) {
      id = crypto.randomUUID()
      sessionStorage.setItem(SESSION_ID_KEY, id)
    }
    sessionId = id
  } catch {
    // sessionStorage indisponível (ex.: modo privado restrito) — segue com
    // um id só de memória, válido pra esta execução (não sobrevive a um F5)
    sessionId = crypto.randomUUID()
  }
  return sessionId
}

/** Rótulo simples de navegador/SO pra exibição em Admin → Sessões — não
 *  precisa de parsing robusto de user-agent, só o suficiente pra diferenciar
 *  abas/dispositivos na lista. */
function friendlyDeviceLabel(): string {
  const ua = navigator.userAgent
  const browser =
    ua.includes('Edg/')     ? 'Edge'    :
    ua.includes('OPR/')     ? 'Opera'   :
    ua.includes('Chrome/')  ? 'Chrome'  :
    ua.includes('Firefox/') ? 'Firefox' :
    ua.includes('Safari/')  ? 'Safari'  : 'Navegador'
  const os =
    ua.includes('Windows')                        ? 'Windows' :
    ua.includes('Mac OS')                          ? 'macOS'   :
    ua.includes('Android')                         ? 'Android' :
    ua.includes('iPhone') || ua.includes('iPad')   ? 'iOS'     :
    ua.includes('Linux')                           ? 'Linux'   : ''
  return os ? `${browser} · ${os}` : browser
}

/**
 * Registra a sessão (idempotente — re-registrar só atualiza last_seen_at) e
 * arma o heartbeat periódico. Chamado uma única vez em App.vue, mesmo padrão
 * de useForceLogout()/useBroadcast() (watch(authUser), immediate).
 */
export async function startSessionHeartbeat(user: AuthUser, store: Store<any>, router: Router): Promise<void> {
  const db = useAdminDB()
  const id = getSessionId()

  setSessionRevokeResolver(() => db.clearSessionRevoke(id))

  const beat = async () => {
    try {
      const revoked = await db.heartbeatSession(id)
      if (revoked) armForceLogoutWarning('session', store, router)
    } catch (err) {
      console.warn('[SessionTracking] heartbeat:', err)
    }
  }

  try {
    await db.registerSession(id, user.id, user.email, user.name, friendlyDeviceLabel())
  } catch (err) {
    console.warn('[SessionTracking] registerSession:', err)
  }

  if (heartbeatTimer) return
  heartbeatTimer = setInterval(beat, HEARTBEAT_MS)
}

export function stopSessionHeartbeat(): void {
  if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null }
  setSessionRevokeResolver(null)
}

/**
 * Chamado por performLogout (useAutoLogout.ts) — cobre TODO caminho de saída
 * (idle, manual, forçado por admin, sessão revogada), não só o feliz. Sem
 * isso, a linha em agent_sessions ficaria pra sempre com last_seen_at parado
 * no último heartbeat, indistinguível de uma aba fechada sem logout.
 */
export async function closeCurrentSession(): Promise<void> {
  stopSessionHeartbeat()
  try {
    await useAdminDB().closeSession(getSessionId())
  } catch (err) {
    console.warn('[SessionTracking] closeSession:', err)
  }
}
